import React from 'react';
import styled from '@emotion/styled';
import IFrameResizer from 'iframe-resizer-react';
import {
  BUTTON_EDGE_OFFSET,
  DEFAULT_CHAT_HEIGHT,
  DEFAULT_CHAT_WIDTH,
  DEFAULT_LAUNCHER_COLOUR,
  SMALL_DEVICE_WIDTH,
} from '../../contants';
import type { AssistantConfigWindow } from '../../types';
import { useMaintenanceMode, useReloadChat, useWindowMessage, useWindowMessageClose, useWindowSize } from './hooks';
import { getUrl } from './utils';
import type { IconCornerStyles } from './utils/getIconCornerStyles';
import {
  getIconCornerStyles,
  normalizeIconCorner,
  normalizeIconOffset,
  normalizeLauncherZIndex,
} from './utils/getIconCornerStyles';

declare let window: AssistantConfigWindow;

const Container = styled.div<{
  hidden: boolean;
  cornerStyles: IconCornerStyles;
  zIndex: number;
}>`
  position: fixed;
  top: ${({ cornerStyles }) => cornerStyles.top};
  right: ${({ cornerStyles }) => cornerStyles.right};
  bottom: ${({ cornerStyles }) => cornerStyles.bottom};
  left: ${({ cornerStyles }) => cornerStyles.left};
  z-index: ${({ zIndex }) => zIndex};
  display: ${(props) => (props.hidden ? 'none' : 'flex')};
  flex-direction: ${({ cornerStyles }) => cornerStyles.flexDirection};
`;

const Button = styled.button<{ colour: string }>`
  background-color: ${(props) => props.colour};
  font-size: 18px;
  height: 64px;
  width: 64px;
  border-radius: 50%;
  border-width: 0;
  color: white;
`;

const EmbedIconButton = styled.button`
  background-color: transparent;
  border-width: 0;
  img {
    height: 96px;
    width: 96px;
  }
`;

export const App: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  // The iframe boots the full chat app (bundle download plus tenant/strings/flags
  // API calls and guest session creation), so it is not mounted until the visitor
  // first opens the chat. Once opened it stays mounted so closing and reopening
  // does not reboot the app or lose the conversation.
  const [hasOpened, setHasOpened] = React.useState(false);
  const iframeRef = React.useRef(null);
  const { width, height } = useWindowSize();
  const config = useWindowMessage();
  const isMaintenanceMode = useMaintenanceMode();
  // reload key is used to force a re-render of the iframe
  const reloadKey = useReloadChat();
  useWindowMessageClose(() => setIsOpen(false));

  const scripts = document.getElementById('assistant-config') as HTMLScriptElement;
  const domain = scripts?.src.split('/')[2];

  const iconCorner = normalizeIconCorner(window.assistantConfig?.iconCorner);
  const iconOffset = normalizeIconOffset(window.assistantConfig?.iconOffset);
  const launcherZIndex = normalizeLauncherZIndex(window.assistantConfig?.launcherZIndex);
  const isSmallDevice = width < SMALL_DEVICE_WIDTH || height < DEFAULT_CHAT_HEIGHT + BUTTON_EDGE_OFFSET;
  const cornerStyles = getIconCornerStyles(iconCorner, isSmallDevice, iconOffset);
  const hidden = !isOpen ? { hidden: true } : {};
  const { embed } = config?.tenantDisplay?.logos || {};

  // Host-page launcher branding, used until the tenant config arrives from the
  // chat iframe (which is only mounted once the visitor first opens the chat).
  const launcherIcon =
    typeof window.assistantConfig?.launcherIcon === 'string' ? window.assistantConfig.launcherIcon : undefined;
  const launcherColour =
    typeof window.assistantConfig?.launcherColour === 'string' ? window.assistantConfig.launcherColour : undefined;
  const embedIconSrc = embed ? `https://${domain || process.env.PROXY_TARGET}${embed}` : launcherIcon;

  const openChat = () => {
    setHasOpened(true);
    setIsOpen(true);
  };

  // if the main page is resized, send the new size to the iframe
  React.useEffect(() => {
    iframeRef?.current?.sendMessage({ height, width, isSmallDevice });
  }, [height, width, isSmallDevice, hasOpened]);

  return (
    <Container cornerStyles={cornerStyles} zIndex={launcherZIndex} hidden={isMaintenanceMode} key={reloadKey}>
      {hasOpened && (
        <IFrameResizer
          id="assistant-webchat-container"
          checkOrigin={false}
          autoResize={false}
          height={isSmallDevice ? height : DEFAULT_CHAT_HEIGHT + 8}
          sizeHeight={false}
          forwardRef={iframeRef}
          {...hidden}
          src={getUrl()}
          bodyMargin={0}
          style={{
            minWidth: '100%',
            borderWidth: 0,
            width: isSmallDevice ? width : DEFAULT_CHAT_WIDTH + 8,
          }}
        />
      )}
      {!isOpen && embedIconSrc && (
        <EmbedIconButton onClick={openChat}>
          <img alt="chat" src={embedIconSrc} />
        </EmbedIconButton>
      )}
      {!isOpen && !embedIconSrc && (
        <Button
          colour={config?.tenantDisplay.palette.primaryMain || launcherColour || DEFAULT_LAUNCHER_COLOUR}
          onClick={openChat}
        >
          chat
        </Button>
      )}
    </Container>
  );
};
